/**
 * The slot for the two `EvidenceAnswerPorts` members ruling 62 forbids moving:
 * `getPartnerConfig` (`src/components/chat/constants/integrationPartners.ts`,
 * which reads the query cache during render and has fifteen importers) and
 * `sourceActionsComponent` (`SourceCardActions`, with its twelve-file feedback
 * subgraph and one of K8's two app-only blockers).
 *
 * Both live inside the Chat quarantine globs, and a file outside Chat naming
 * either is a NEW inbound pair on a set that is shrink-only in both directions.
 * So Chat fills the slot — `src/components/chat/components/evidence-answer-ports.ts`
 * calls the registrar at module scope — and the host beside this file reads it,
 * which leaves every composition point free of a Chat import.
 *
 * A separate module from the host so the host exports components only
 * (react-refresh).
 */

import type { EvidenceAnswerPorts } from '@heidi/evidence';

/** The two members only Chat may name. */
export type EvidenceAnswerChatPorts = Pick<
  EvidenceAnswerPorts,
  'getPartnerConfig' | 'sourceActionsComponent'
>;

let registered: EvidenceAnswerChatPorts | null = null;

/** Called ONCE at module scope, from inside the Chat quarantine globs. */
export const registerEvidenceAnswerChatPorts = (ports: EvidenceAnswerChatPorts): void => {
  registered = ports;
};

/**
 * Read the pair. Throws rather than degrading: an answer surface with no
 * partner branding and no source actions renders as a plausible card, so a
 * missing registration has to be loud.
 */
export const resolveEvidenceAnswerChatPorts = (): EvidenceAnswerChatPorts => {
  if (registered === null) {
    throw new Error(
      'EvidenceAnswerPorts: getPartnerConfig and sourceActionsComponent are unregistered — ' +
        'src/components/chat/components/evidence-answer-ports.ts has not been loaded.'
    );
  }
  return registered;
};

/** Test-only: drop the registered pair between tests. */
export const __resetEvidenceAnswerChatPortsForTests = (): void => {
  registered = null;
};
